import { exists } from '@treasury/utils';
import { css } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef } from 'lit/directives/ref.js';
import { TmFormControl, TmFormControlDefault } from './forms/tm-form';

export abstract class TmInputFieldBase<T>
    extends TmFormControl<T>
    implements TmFormControlDefault<T>
{
    @property({ type: String })
    public label?: string;

    @property({ type: String })
    public placeholder?: string;

    @property({ type: Boolean })
    public readonly = false;

    @property({ type: Boolean })
    public disabled = false;

    @property({ type: String })
    public helperText?: string;

    @property({ type: Boolean, reflect: true })
    public noOuterSpacing = false;

    @property({ attribute: false })
    public default?: T;

    /**
     * Represents the primary text input element for this component.
     */
    protected readonly inputFieldRef = createRef<HTMLElement & { value: string }>();

    protected get inputField() {
        return this.inputFieldRef.value;
    }

    public connectedCallback() {
        super.connectedCallback();
        this.listenForValueChange(({ value }) => {
            if (!this.inputField || typeof value !== 'string' || this.inputField.value === value) {
                return;
            }
            // we have to set this explicitly since Vaadin controls don't use reactive props
            this.inputField.value = value || '';
        });
    }

    protected update(changedProperties: Map<PropertyKey, unknown>): void {
        if (exists(this.value)) {
            this.setAttribute('has-value', '');
        } else {
            this.removeAttribute('has-value');
        }

        if (exists(this.placeholder)) {
            this.setAttribute('has-placeholder', '');
        } else {
            this.removeAttribute('has-placeholder');
        }

        super.update(changedProperties);
    }

    public static get styles() {
        return [
            css`
                :host {
                    position: relative;
                    --input-field-text: var(--jh-color-gray-950);
                    --input-field-background: var(--jh-color-gray-50);
                    --input-field-background-invalid: var(--jh-color-red-50);
                    --input-field-border: var(--jh-color-gray-300);
                    --input-field-disabled: var(--lumo-contrast-30pct);
                    --input-field-placeholder: var(--jh-color-gray-600);
                }
                :host([theme='dark']) {
                    --input-field-text: var(--jh-color-gray-0);
                    --input-field-background: rgba(8, 20, 39, 0.7);
                    --input-field-background-invalid: rgba(201, 29, 7, 0.15);
                    --input-field-border: var(--tm-color-gray-light-30);
                    --input-field-disabled: var(--tm-color-gray-400-50);
                    --input-field-placeholder: var(--jh-color-gray-450);
                }

                :host([noOuterSpacing]) {
                    --lumo-space-xs: 0;
                }

                :host(not:([noOuterSpacing])) {
                    margin: 4px 0 8px 0;
                }

                *::part(input-field) {
                    background: var(--input-field-background);
                    border-color: var(--input-field-border);
                    color: var(--input-field-text);
                    font-size: 1rem;
                    font-family: var(--default-font-family);
                    font-weight: 400;
                    padding-left: 10px;
                    --lumo-secondary-text-color: var(--secondary-text-color);
                    --lumo-disabled-text-color: var(--input-field-disabled);
                }

                *::part(input-field)::after {
                    background: none;
                }

                *::part(label) {
                    position: absolute;
                    z-index: 1;
                    top: 22px;
                    left: 8px;
                    background: none;
                    transition: all 0.2s cubic-bezier(0.1, 0.5, 0.1, 1) 0s;
                    pointer-events: none;
                    font-size: 14px;
                    font-family: var(--default-font-family);
                    --vaadin-input-field-label-color: var(--tertiary-text-color);
                    --vaadin-input-field-focused-label-color: var(--content-action);
                    --lumo-disabled-text-color: var(--input-field-disabled);
                }

                *::part(toggle-button),
                *::part(remove-button) {
                    color: var(--primary-text-color);
                }

                :host(:not([noOuterSpacing]))::part(component) {
                    padding-top: 10px;
                }

                :host::part(component)::before {
                    display: none;
                }

                *[focused]::part(label),
                *[has-value]::part(label),
                *[has-placeholder]::part(label) {
                    padding-left: 4px;
                    padding-top: 4px;
                    padding-bottom: 4px;
                    background: var(--input-field-background);
                    border-radius: 3px;
                    font-size: 11px;
                    left: 4px;
                    top: 1px;
                }

                *[focused]:not([required])::part(label),
                *[has-value]:not([required])::part(label),
                *[has-placeholder]:not([required])::part(label) {
                    padding-right: 3px;
                }

                *[invalid][focused]::part(label)::after,
                *[invalid][has-value]::part(label)::after,
                *[invalid][has-placeholder]::part(label)::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: inherit;
                    pointer-events: none;
                }

                *[focused]::part(input-field) {
                    border-color: var(--brand-primary);
                }

                vaadin-text-field.invalid::part(input-field),
                *[invalid]::part(input-field) {
                    color: var(--content-negative);
                    border-color: var(--content-negative);
                    background: var(--input-field-background-invalid);
                }

                .invalid::part(label),
                *[invalid]::part(label) {
                    color: var(--content-negative);
                }

                vaadin-text-field > input:placeholder-shown {
                    color: var(--input-field-placeholder);
                }

                :host(.helper-align-right) *::part(helper-text) {
                    text-align: right;
                    padding-right: 1em;
                }

                :host(.form-row-slotted) {
                    margin: 8px 0 8px;
                }

                :host(.form-row-slotted)::part(component) {
                    padding: 0px;
                }

                :host(.form-row-slotted)::part(input-field) {
                    border: none;
                    border-bottom: 1px solid var(--input-field-border);
                    background-color: var(--input-field-background);
                    border-radius: 4px 4px 0 0;
                }

                :host(.form-row-slotted) *[invalid]::part(input-field) {
                    background: var(--input-field-background-invalid);
                    border-color: var(--content-negative);
                    color: var(--content-negative);
                }

                vaadin-password-field::part(reveal-button)::before {
                    color: var(--secondary-text-color);
                }
            `,
        ];
    }
}
